import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  serverTimestamp,
  setDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase/config";

/** @typedef {import('../domain/matchHistory/matchHistoryTypes').MatchRecord} MatchRecord */
/** @typedef {import('../domain/matchHistory/matchHistoryTypes').MatchParticipant} MatchParticipant */
/** @typedef {import('../domain/matchHistory/matchHistoryTypes').PlayerHistoryQuery} PlayerHistoryQuery */

const RECORDS = "matchRecords";
const PARTICIPANTS = "matchParticipants";

function isFirestoreIndexUnavailable(err) {
  const code = String(err?.code || "");
  const msg = String(err?.message || "");
  return (
    code === "failed-precondition" ||
    /requires an index|index is currently building|cannot be used yet/i.test(msg)
  );
}

function participantMatchesSport(row, sportId) {
  if (!sportId) return true;
  const sid = String(row?.sportId || "badminton").trim().toLowerCase();
  return sid === String(sportId).trim().toLowerCase();
}

function recordRef(matchId) {
  return doc(db, RECORDS, matchId);
}

function toMillis(value) {
  if (value == null) return 0;
  if (typeof value === "number") return value;
  if (typeof value.toMillis === "function") return value.toMillis();
  return 0;
}

/**
 * @param {Record<string, unknown>} data
 * @param {string} id
 * @returns {MatchRecord}
 */
function recordFrom(data, id) {
  return { ...data, matchId: id };
}

/**
 * @param {string} matchId
 */
export async function findRecordById(matchId) {
  const snap = await getDoc(recordRef(matchId));
  if (!snap.exists()) return null;
  return recordFrom(snap.data(), snap.id);
}

/**
 * @param {string} idempotencyKey
 */
export async function findRecordByIdempotencyKey(idempotencyKey) {
  const q = query(
    collection(db, RECORDS),
    where("idempotencyKey", "==", idempotencyKey),
    limit(1)
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return recordFrom(d.data(), d.id);
}

/**
 * @param {MatchRecord} record
 */
export async function createRecord(record) {
  const ref = recordRef(record.matchId);
  const payload = {
    ...record,
    createdAt: serverTimestamp(),
    completedAt: serverTimestamp(),
    completedAtMs: record.completedAt ?? Date.now(),
  };
  await setDoc(ref, payload);
  const created = await findRecordById(record.matchId);
  if (!created) throw new Error("Match record was not created");
  return created;
}

/**
 * @param {MatchParticipant[]} participants
 */
export async function createParticipants(participants) {
  if (!participants.length) return;
  const batch = writeBatch(db);
  participants.forEach((p) => {
    const ref = doc(db, PARTICIPANTS, p.participantId);
    batch.set(ref, {
      ...p,
      completedAt: serverTimestamp(),
      completedAtMs: typeof p.completedAt === "number" ? p.completedAt : Date.now(),
    });
  });
  await batch.commit();
}

/**
 * @param {string} matchId
 */
export async function listParticipantsForMatch(matchId) {
  const q = query(collection(db, PARTICIPANTS), where("matchId", "==", matchId));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ ...d.data(), participantId: d.id }));
}

/**
 * Load participants for multiple matches (Firestore `in` queries, max 10 ids each).
 * @param {string[]} matchIds
 */
export async function listParticipantsForMatchIds(matchIds) {
  const unique = [...new Set((matchIds || []).filter(Boolean))];
  if (!unique.length) return [];
  /** @type {MatchParticipant[]} */
  const rows = [];
  for (let i = 0; i < unique.length; i += 10) {
    const chunk = unique.slice(i, i + 10);
    const q = query(collection(db, PARTICIPANTS), where("matchId", "in", chunk));
    const snap = await getDocs(q);
    snap.docs.forEach((d) => {
      rows.push({ ...d.data(), participantId: d.id });
    });
  }
  return rows;
}

/**
 * @param {PlayerHistoryQuery} options
 */
export async function listParticipantsForPlayer(options) {
  const {
    playerId,
    limit: max = 20,
    startAfter: cursorDoc,
    matchType,
    sportId,
    competitionId,
    clubId,
    result,
  } = options;

  const pageSize = Math.min(max, 50);

  /**
   * @param {{ useSportEquality: boolean }} mode
   */
  async function runQuery({ useSportEquality }) {
    const constraints = [where("playerId", "==", playerId), orderBy("completedAtMs", "desc")];
    if (matchType) constraints.push(where("matchType", "==", matchType));
    if (useSportEquality && sportId) constraints.push(where("sportId", "==", sportId));
    if (competitionId) constraints.push(where("competitionId", "==", competitionId));
    if (clubId) constraints.push(where("clubId", "==", clubId));
    if (result) constraints.push(where("result", "==", result));

    const clientFilterSport = Boolean(sportId) && !useSportEquality;
    const fetchLimit = clientFilterSport ? Math.min(Math.max(pageSize * 5, pageSize), 50) : pageSize;
    const parts = [collection(db, PARTICIPANTS), ...constraints, limit(fetchLimit)];
    if (cursorDoc) parts.push(startAfter(cursorDoc));
    const q = query(...parts);
    const snap = await getDocs(q);
    let items = snap.docs.map((d) => ({ ...d.data(), participantId: d.id }));
    if (clientFilterSport) {
      items = items.filter((row) => participantMatchesSport(row, sportId)).slice(0, pageSize);
    }
    const lastDoc = snap.docs[snap.docs.length - 1] || null;
    return { items, lastDoc };
  }

  try {
    return await runQuery({ useSportEquality: Boolean(sportId) });
  } catch (err) {
    // Composite sport index may be missing or still building — keep Home usable.
    if (sportId && isFirestoreIndexUnavailable(err)) {
      return runQuery({ useSportEquality: false });
    }
    throw err;
  }
}

export { RECORDS as MATCH_RECORDS_COLLECTION, PARTICIPANTS as MATCH_PARTICIPANTS_COLLECTION, toMillis };
